import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Mail, Lock, ArrowRight, Loader2, FlaskConical } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const from = location.state?.from?.pathname || '/';

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      await login(email, password);
      toast.success('Welcome back');
      navigate(from, { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F2E8] pt-24 pb-24 flex items-center justify-center">
      <div className="w-full max-w-md px-4">
        <div className="bg-white p-8 border-2 border-[#E0DDD4]">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-12 h-12 bg-[#4A7C29] flex items-center justify-center mx-auto mb-4">
              <FlaskConical className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-xl font-semibold text-[#1A1A1A] mb-2 font-['IBM_Plex_Sans_Condensed'] uppercase tracking-wide">
              AUTHENTICATION PORTAL
            </h1>
            <p className="text-[#5A5A5A] text-sm font-['IBM_Plex_Sans']">
              Access your TastyJelly Global Industries account
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-[#1A1A1A] text-xs font-medium font-['IBM_Plex_Sans_Condensed'] uppercase tracking-wide">
                EMAIL ADDRESS
              </Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5A5A5A]" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@institution.org"
                  className="pl-10 rounded-none border-2 border-[#E0DDD4] bg-[#F5F2E8] h-11 font-['IBM_Plex_Sans'] focus:border-[#4A7C29]"
                  data-testid="login-email"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-[#1A1A1A] text-xs font-medium font-['IBM_Plex_Sans_Condensed'] uppercase tracking-wide">
                PASSWORD
              </Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5A5A5A]" />
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pl-10 rounded-none border-2 border-[#E0DDD4] bg-[#F5F2E8] h-11 font-['IBM_Plex_Sans'] focus:border-[#4A7C29]"
                  data-testid="login-password"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full rounded-none bg-[#4A7C29] text-white font-semibold py-5 hover:bg-[#3D6822] transition-colors disabled:opacity-50 font-['IBM_Plex_Sans_Condensed'] uppercase tracking-wider text-xs"
              data-testid="login-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  AUTHENTICATING...
                </>
              ) : (
                <>
                  SIGN IN
                  <ArrowRight className="ml-2 w-4 h-4" />
                </>
              )}
            </Button>
          </form>

          {/* Register Link */}
          <p className="text-center mt-6 text-[#5A5A5A] text-sm font-['IBM_Plex_Sans']">
            Don't have an account?{' '}
            <Link 
              to="/register" 
              className="text-[#4A7C29] font-semibold hover:underline"
              data-testid="register-link"
            >
              Create Account
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
